import React from 'react'
import { useAppSelector } from 'app/hooks'
import {
    AdminView,
    RootState,
    NestedDict,
    FieldType,
    ValidateType,
} from 'app/types'
import { validateOrganization } from 'helpers/authHeader'
import { AdminEditor } from 'views/AdminPage/AdminEditor'
import * as fnc from 'helpers/fnc'

export const defaultOrganizationMetadata = {
    id: null,
    name: '',
    link: '',
    domain: '',
    phone: '',
    email: '',
    tagline: '',
    subtagline: '',
    missionStatement: '',
    published: false,
    themeId: null,
    colorSchemeId: null,
    hotjarId: null,
    googleTagId: null,
    googleTagManagerId: null,
    facebookPixelId: null,
    hubspotTrackingId: null,
    hubspotAccessToken: null,
    hubspotClientSecret: null,
    mediaId: null,
    logoMediaId: null,
    logoNotextMediaId: null,
    logoTopbarMediaId: null,
    faviconMediaId: null,
    splashMediaId: null,
    splashGalleryId: null,
    twitterLink: '',
    facebookLink: '',
    instagramLink: '',
    linkedinLink: '',
    youtubeLink: '',
    hashtag: '',
    bookingEmail: '',
    consent: '',
    enableBrowseCommunities: true,
    enableRequestInfo: false,
    requestInfoConfig: null,
}

interface OrganizationMetadataEditorProps {
    organization?: any
    delta: NestedDict
    onChange: () => void
    onSubmit: () => void
    checkChange: () => void
    mode?: 'full' | 'organization-only' // 'full' for OrganizationsView, 'organization-only' for OrganizationInfoView
}

export function OrganizationMetadataEditor(props: OrganizationMetadataEditorProps) {
    const {
        organization,
        delta,
        onChange,
        onSubmit,
        checkChange,
        mode = 'full'
    } = props

    const config = useAppSelector((state: RootState) => state.app.config)
    const { themes, colorSchemes } = config

    // Handle change events - need to call parent onChange with the change data
    const handleChange = (editData) => {
        if (onChange && mode === 'organization-only') {
            // For organization-only mode, only include fields that have actually been modified
            const changes = {}
            
            // Compare each field to detect ACTUAL changes (not just different from defaults)
            Object.keys(editData).forEach(key => {
                const originalValue = organization?.[key]
                const newValue = editData[key]
                
                // Only include in changes if:
                // 1. The value is actually different from the original
                // 2. AND it's not just a null/undefined conversion (which isn't a real change)
                if (JSON.stringify(originalValue) !== JSON.stringify(newValue)) {
                    // Don't include changes where we're just converting undefined to null
                    if (!(originalValue === undefined && newValue === null)) {
                        // Don't include changes where we're converting null to empty string or vice versa
                        if (!(
                            (originalValue === null && newValue === '') || 
                            (originalValue === '' && newValue === null) ||
                            (originalValue === undefined && newValue === '')
                        )) {
                            changes[key] = newValue
                            
                        }
                    }
                }
            })
            
            
            
            // Merge with existing delta to accumulate changes, but only real changes
            const accumulatedChanges = {
                ...delta, // Keep existing changes
                ...changes // Add new changes
            }
            
            
            
            // Remove any fields that have been reverted to original values
            Object.keys(accumulatedChanges).forEach(key => {
                const originalValue = organization?.[key]
                const currentValue = editData[key]
                
                // If current value matches original, remove from accumulated changes
                if (JSON.stringify(originalValue) === JSON.stringify(currentValue)) {
                    
                    delete accumulatedChanges[key]
                }
                
                // Also remove if it's a meaningless null/undefined/empty conversion
                if (
                    (originalValue === undefined && currentValue === null) ||
                    (originalValue === null && currentValue === '') ||
                    (originalValue === undefined && currentValue === '') ||
                    (originalValue === '' && currentValue === null)
                ) {
                    
                    delete accumulatedChanges[key]
                }
            })
            
            // Only call onChange if there are meaningful changes
            if (Object.keys(accumulatedChanges).length > 0) {
                onChange(accumulatedChanges)
            } else {
                
                onChange(null) // Reset delta if no meaningful changes
            }
        } else if (onChange) {
            
            // For full mode, pass through the editData as-is
            onChange(editData)
        }
    }

    // Handle submit events
    const handleSubmit = () => {
        
        if (onSubmit) {
            if (mode === 'organization-only') {
                // For organization-only mode, pass the delta object to onSubmit
                return onSubmit(delta)
            } else {
                // For full mode, pass delta as before
                return onSubmit(delta)
            }
        }
        return Promise.resolve(true)
    }

    // Define all metadata fields (available in both modes)
    const allMetadataFields = {
        name: {
            type: FieldType.String,
            label: 'Organization Name'
        },
        link: {
            type: FieldType.String,
            label: 'Organization Link'
        },
        domain: {
            type: FieldType.String,
            label: 'Domain',
            list: false,
        },
        phone: {
            type: FieldType.String,
            label: 'Phone Number',
            validation: ValidateType.Phone,
        },
        email: {
            type: FieldType.String,
            label: 'Email Address',
            validation: ValidateType.Email,
        },
        tagline: {
            type: FieldType.String,
            label: 'Tagline'
        },
        subtagline: {
            type: FieldType.String,
            label: 'Sub-tagline'
        },
        missionStatement: {
            type: FieldType.String,
            label: 'Mission Statement',
            textarea: true
        },
        published: {
            header: 'Configuration',
            type: FieldType.Toggle,
            label: 'Published',
            text: 'Visible to Public'
        },
        enableBrowseCommunities: {
            type: FieldType.Toggle,
            label: 'Enable Browse Communities',
            text: 'Show Browse Communities Button'
        },
        enableRequestInfo: {
            type: FieldType.Toggle,
            label: 'Enable Request Info',
            text: 'Show Request Info Button',
            list: false,
        },
        requestInfoConfig: {
            type: FieldType.String,
            label: 'Request Info Config',
            textarea: true,
            list: false,
        },
        themeId: {
            header: 'Theme',
            label: 'Theme',
            type: FieldType.Option,
            list: false,
            options: themes ? [{ id: null, name: 'None' }, ...themes] : [],
        },
        colorSchemeId: {
            label: 'Color Scheme',
            type: FieldType.Option,
            list: false,
            options: colorSchemes ? [{ id: null, name: 'None' }, ...colorSchemes] : [],
            optionMap: (x) => {
                const colors = [x.primaryColor, x.secondaryColor, x.tertiaryColor, x.backgroundColor]
                const schemeSummary = colors.map((x, i) => <div key={i} className="color-swatch" style={{ backgroundColor: `#${x}` }} />)
                return {
                    text: <React.Fragment>{x.name} <div className="swatches">{schemeSummary}</div></React.Fragment>,
                    value: x.id,
                }
            },
        },
        mediaId: {
            header: 'Media',
            label: 'Main Media',
            type: FieldType.Media,
        },
        logoMediaId: {
            label: 'Logo Media',
            type: FieldType.Media,
        },
        logoNotextMediaId: {
            label: 'Logo (No Text)',
            type: FieldType.Media,
            list: false,
        },
        logoTopbarMediaId: {
            label: 'Top Bar Logo',
            type: FieldType.Media,
            list: false,
        },
        faviconMediaId: {
            label: 'Favicon',
            type: FieldType.Media,
            list: false,
        },
        splashMediaId: {
            label: 'Splash Media',
            type: FieldType.Media,
            list: false,
        },
        twitterLink: {
            header: 'Social Media',
            label: 'Twitter Link',
            type: FieldType.String,
            list: false,
        },
        facebookLink: {
            label: 'Facebook Link',
            type: FieldType.String,
            list: false,
        },
        instagramLink: {
            label: 'Instagram Link',
            type: FieldType.String,
            list: false,
        },
        linkedinLink: {
            label: 'LinkedIn Link',
            type: FieldType.String,
            list: false,
        },
        youtubeLink: {
            label: 'YouTube Link',
            type: FieldType.String,
            list: false,
        },
        hashtag: {
            label: 'Hashtag',
            type: FieldType.String,
            list: false,
        },
        bookingEmail: {
            header: 'Contact Information',
            label: 'Booking Email',
            type: FieldType.String,
            validation: ValidateType.Email,
            list: false,
        },
        consent: {
            label: 'Consent Text',
            type: FieldType.String,
            textarea: true,
            list: false,
        },
        hotjarId: {
            header: 'Analytics',
            label: 'Hotjar ID',
            type: FieldType.String,
            list: false,
            adminOnly: true,
        },
        googleTagId: {
            label: 'Google Analytics ID',
            type: FieldType.String,
            list: false,
            adminOnly: true,
        },
        googleTagManagerId: {
            label: 'Google Tag Manager ID',
            type: FieldType.String,
            list: false,
            adminOnly: true,
        },
        facebookPixelId: {
            label: 'Facebook Pixel ID',
            type: FieldType.String,
            list: false,
            adminOnly: true,
        },
        hubspotTrackingId: {
            header: 'HubSpot Integration',
            label: 'HubSpot Tracking ID',
            type: FieldType.String,
            list: false,
            adminOnly: true,
        },
        hubspotAccessToken: {
            label: 'HubSpot Access Token',
            type: FieldType.String,
            list: false,
            adminOnly: true,
        },
        hubspotClientSecret: {
            label: 'HubSpot Client Secret',
            type: FieldType.String,
            list: false,
            adminOnly: true,
        },
    }

    // Use all fields for both modes - the distinction is in data handling, not field visibility
    const fields = allMetadataFields

    // For organization-only mode, we need to use the current organization as the data source
    const targetView = mode === 'full' ? AdminView.Organizations : AdminView.Organizations
    
    // Use organization data directly for organization-only mode, merged with delta
    const editData = mode === 'organization-only' && organization ? {
        ...defaultOrganizationMetadata, // Start with defaults
        ...organization, // Add organization data
        ...delta, // Apply accumulated changes from delta
        id: organization.id,
        name: organization.name,
        link: organization.link,
    } : undefined

    return <AdminEditor
        view={targetView}
        validation={validateOrganization}
        checkChange={checkChange}
        onChange={handleChange}
        onSubmit={handleSubmit}
        fields={fields}
        newData={mode === 'full' ? defaultOrganizationMetadata : undefined}
        editData={editData}
        compactMode={mode === 'organization-only'}
    />
}